import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { useOutletContext, useParams } from 'react-router';
import { useChargement } from '../coquille/useChargement.js';
import { poserConfiguration, supprimerConfiguration,
         type EntreeConfiguration } from '../environnements.js';
import { EchecApi } from '../api.js';
interface Espace { readonly code: string; readonly nom: string; readonly role: string }
interface ReponseConfiguration { readonly configuration: readonly EntreeConfiguration[] }
type Nature = 'test' | 'production';
const LIBELLE: Record<Nature, string> = { test: 'Test', production: 'Production' };
type Envoi =
  | { readonly etat: 'repos' }
  | { readonly etat: 'en_vol' }
  | { readonly etat: 'fait'; readonly cle: string; readonly secrete: boolean }
  | { readonly etat: 'echec'; readonly message: string };
/**
 * LA CONFIGURATION D UN ENVIRONNEMENT [29-H].
 *
 * ╔═══════════════════════════════════════════════════════════════════════╗
 * ║ UN SEUL CONTEXTE A L ECRAN : TEST **OU** PRODUCTION.                  ║
 * ╚═══════════════════════════════════════════════════════════════════════╝
 *
 * L environnement se designe par sa NATURE dans l URL -- le couple
 * (projet, nature) est l identite publique [28-CC]. On n affiche jamais les
 * deux configurations cote a cote : on ecrit ici des secrets, et deux
 * contextes simultanes invitent a poser une valeur du mauvais cote.
 *
 * ── UN SECRET NE SE RELIT JAMAIS ────────────────────────────────────────
 *
 * Le serveur ne projette PAS la valeur d une entree secrete, ni aucun
 * fragment : `valeur` est simplement absente. L ecran dit donc « secrète »
 * et propose de REMPLACER, jamais de consulter. Ce n est pas un masquage
 * d affichage -- la donnee n arrive pas.
 *
 * ── CLAIRE OU SECRETE, JAMAIS LES DEUX ──────────────────────────────────
 *
 * Miroir du CHECK d exclusion de la base : le formulaire porte UN choix de
 * nature, jamais deux champs qu on pourrait remplir ensemble.
 *
 * `environnement.configurer` est reservee a administrateur et proprietaire :
 * la configuration porte les URL, identifiants et secrets employes a
 * l execution. Le masquage n est qu une traduction ergonomique du droit --
 * la route reste l autorite.
 */
export default function ConfigurationEnvironnement(): ReactElement {
  const espace = useOutletContext<Espace>();
  const { projet, nature } = useParams<{ projet: string; nature: string }>();
  const p = projet ?? '';
  const n: Nature = nature === 'test' ? 'test' : 'production';
  const { etat, recharger } = useChargement<ReponseConfiguration>(
    `/locataires/${encodeURIComponent(espace.code)}`
    + `/projets/${encodeURIComponent(p)}`
    + `/environnements/${encodeURIComponent(n)}/configuration`);
  const [cle, poserCle] = useState('');
  const [valeur, poserValeur] = useState('');
  const [secrete, poserSecrete] = useState(false);
  const [envoi, poserEnvoi] = useState<Envoi>({ etat: 'repos' });
  // La reponse tardive n ecrit pas sur un ecran quitte. useChargement garde
  // la LECTURE ; la mutation se garde elle-meme.
  const monte = useRef(true);
  useEffect(() => () => { monte.current = false; }, []);
  const peutConfigurer = espace.role === 'administrateur' || espace.role === 'proprietaire';
  const surPoser = useCallback(() => {
    poserEnvoi({ etat: 'en_vol' });
    void (async () => {
      try {
        const corps = secrete ? { secret: valeur } : { valeur };
        const r = await poserConfiguration(espace.code, p, n, cle, corps);
        if (!monte.current) return;
        poserEnvoi({ etat: 'fait', cle: r.cle, secrete: r.secrete });
        poserCle(''); poserValeur(''); poserSecrete(false);
        recharger();
      } catch (e) {
        if (!monte.current) return;
        poserEnvoi({ etat: 'echec', message: e instanceof EchecApi
          ? e.detail.message : 'Le service est injoignable.' });
      }
    })();
  }, [espace.code, p, n, cle, valeur, secrete, recharger]);
  const surSupprimer = useCallback((c: string) => {
    poserEnvoi({ etat: 'en_vol' });
    void (async () => {
      try {
        await supprimerConfiguration(espace.code, p, n, c);
        if (!monte.current) return;
        poserEnvoi({ etat: 'repos' });
        recharger();
      } catch (e) {
        if (!monte.current) return;
        poserEnvoi({ etat: 'echec', message: e instanceof EchecApi
          ? e.detail.message : 'Le service est injoignable.' });
      }
    })();
  }, [espace.code, p, n, recharger]);
  // CLE VALIDE = celle que la base accepte. On refuse ici ce qu elle
  // refuserait, plutot que de lui laisser rendre un message technique.
  const cleValide = /^[A-Z][A-Z0-9_]{2,63}$/.test(cle);
  return (
    <>
      <div className="contenu__entete">
        <div>
          <h1>Configuration · {LIBELLE[n]}</h1>
          <p className="discret">
            Les valeurs propres à cet environnement, lues à l’exécution.
          </p>
        </div>
      </div>
      {etat.phase === 'chargement' && <p className="discret">Chargement…</p>}
      {etat.phase === 'echec' && (
        <div className="carte">
          <p className={etat.code === 'droit_insuffisant'
            ? 'message message--alerte' : 'message message--refus'} role="alert">
            {etat.message}
          </p>
          {etat.code !== 'droit_insuffisant' && (
            <button type="button" className="bouton" onClick={recharger}>Réessayer</button>
          )}
        </div>
      )}
      {envoi.etat === 'echec' && (
        <p className="message message--refus" role="alert">{envoi.message}</p>
      )}
      {envoi.etat === 'fait' && (
        <p className="message message--succes" role="status">
          {envoi.secrete
            ? `« ${envoi.cle} » est enregistrée comme secrète. Sa valeur ne peut plus être relue, seulement remplacée.`
            : `« ${envoi.cle} » est enregistrée.`}
        </p>
      )}
      {etat.phase === 'pret' && etat.donnees.configuration.length === 0 && (
        <div className="carte etat-vide">
          <h3>Aucune valeur configurée</h3>
          <p className="discret">
            Une automatisation déployée ici lira les valeurs que vous ajoutez,
            par <code>config.NOM</code> dans ses paramètres.
          </p>
        </div>
      )}
      {etat.phase === 'pret' && etat.donnees.configuration.length > 0 && (
        <div className="grille">
          {etat.donnees.configuration.map((e) => (
            <article className="carte" key={e.cle}>
              <h2 className="carte__titre">{e.cle}</h2>
              <p className="carte__detail">
                {e.secrete
                  ? <span className="etiquette">secrète</span>
                  : <code>{e.valeur}</code>}
              </p>
              <p className="discret">{dateLisible(e.modifie_le)}</p>
              {peutConfigurer && (
                <button type="button" className="bouton bouton--discret"
                        disabled={envoi.etat === 'en_vol'}
                        onClick={() => { surSupprimer(e.cle); }}>Supprimer</button>
              )}
            </article>
          ))}
        </div>
      )}
      {peutConfigurer && etat.phase === 'pret' && (
        <div className="carte">
          <h2>Ajouter ou remplacer</h2>
          <label htmlFor="cfg-cle">Nom</label>
          <input id="cfg-cle" value={cle} placeholder="URL_CRM"
                 onChange={(ev) => { poserCle(ev.target.value); }} />
          <p className="discret">
            Majuscules, chiffres et tirets bas, trois caractères au moins.
          </p>
          <label htmlFor="cfg-valeur">Valeur</label>
          <input id="cfg-valeur" value={valeur} type={secrete ? 'password' : 'text'}
                 onChange={(ev) => { poserValeur(ev.target.value); }} />
          {/* PAS de htmlFor : l input est IMBRIQUE dans ce label. Les deux
              liaisons ensemble font basculer DEUX fois quand on clique sur
              la case -- elle revient a son etat initial, et le secret part
              en clair. Mesure au smoke, invisible a tsc et aux preuves. */}
          {/* UN BLOC autour du label : sans lui, la case, son texte et le
              bouton tiennent sur UNE ligne et l avertissement se fait
              TRONQUER -- justement celui qui dit que le secret est
              irreversible. Aucun style neuf : la dette 66 reste a 29-M. */}
          <p>
            <label>
              <input id="cfg-secrete" type="checkbox" checked={secrete}
                     onChange={(ev) => { poserSecrete(ev.target.checked); }} />
              Valeur secrète — elle ne pourra plus être relue
            </label>
          </p>
          <button type="button" className="bouton"
                  disabled={!cleValide || valeur === '' || envoi.etat === 'en_vol'}
                  onClick={surPoser}>Enregistrer</button>
        </div>
      )}
    </>
  );
}
/** POSTGRES REND `+00`, PAS `+00:00` NI `Z` : new Date() rendrait NaN. */
function dateLisible(brut: string): string {
  const d = new Date(brut.replace(' ', 'T').replace(/\+00$/, 'Z'));
  return Number.isNaN(d.getTime()) ? brut : d.toLocaleString('fr-BE');
}
